import React from 'react';
import { FaStar, FaSignOutAlt } from 'react-icons/fa';
import { UserData, NavItem } from './types';
 
interface SidebarProps {
  darkMode: boolean;
  userData: UserData;
  navItems: NavItem[];
  activeTab: string;
  currentXP: number;
  nextLevelXP: number;
  onTabChange: (tabId: string) => void;
  onLogout: () => void;
}
 
export const Sidebar: React.FC<SidebarProps> = ({
  darkMode,
  userData,
  navItems,
  activeTab,
  currentXP,
  nextLevelXP,
  onTabChange,
  onLogout,
}) => {
  const xpPct = Math.min(100, Math.round((currentXP / nextLevelXP) * 100));
 
  return (
    <aside
      className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 p-5 overflow-y-auto hidden lg:flex lg:flex-col transition-colors border-r"
      style={{
        background: darkMode ? 'rgba(13,26,26,0.97)' : 'rgba(240,250,250,0.97)',
        backdropFilter: 'blur(12px)',
        borderColor: darkMode ? 'rgba(90,184,184,0.12)' : 'rgba(13,26,26,0.1)',
      }}
    >
      {/* User card */}
      <div
        className="mb-5 p-4 rounded-xl border"
        style={{
          background: darkMode ? '#0f2424' : '#e0f4f4',
          borderColor: darkMode ? 'rgba(90,184,184,0.2)' : 'rgba(13,26,26,0.12)',
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="relative">
            <img
              src={userData.photoURL}
              alt="User"
              className="w-11 h-11 rounded-full"
              style={{ border: '2px solid rgba(90,184,184,0.5)' }}
            />
            {userData.isPremium && (
              <div
                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ background: '#e8a820', border: '2px solid #0d1a1a' }}
              >
                <FaStar style={{ color: '#0d1a1a', fontSize: '7px' }} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="text-sm font-black truncate"
              style={{ color: darkMode ? '#f0fafa' : '#0d1a1a', letterSpacing: '0.5px' }}
            >
              {userData.username}
            </h3>
            <span
              className="text-xs font-bold"
              style={{ color: darkMode ? '#5ab8b8' : '#3a9898' }}
            >
              Level {userData.level}
            </span>
          </div>
        </div>
 
        {/* XP bar */}
        <div
          className="w-full rounded-full h-1.5 overflow-hidden"
          style={{ background: darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.12)' }}
        >
          <div
            className="h-1.5 rounded-full transition-all duration-700"
            style={{ width: `${xpPct}%`, background: '#e8a820' }}
          />
        </div>
        <p
          className="text-[10px] mt-1.5 font-bold"
          style={{ color: darkMode ? 'rgba(126,206,206,0.4)' : 'rgba(13,26,26,0.4)', letterSpacing: '0.5px' }}
        >
          {Math.floor(currentXP)} / {nextLevelXP} XP → Lvl {userData.level + 1}
        </p>
      </div>
 
      {/* Nav items */}
      <nav className="space-y-0.5 flex-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === activeTab;
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm font-bold"
              style={{
                background: isActive
                  ? darkMode ? 'rgba(90,184,184,0.1)' : 'rgba(90,184,184,0.15)'
                  : 'transparent',
                borderLeft: isActive ? '3px solid #e8a820' : '3px solid transparent',
                color: isActive
                  ? '#e8a820'
                  : darkMode ? 'rgba(126,206,206,0.5)' : 'rgba(13,26,26,0.55)',
                letterSpacing: '0.5px',
              }}
            >
              <Icon style={{ fontSize: '16px' }} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
 
      {/* Logout */}
      <button
        onClick={onLogout}
        className="w-full mt-4 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all border text-sm font-bold"
        style={{
          borderColor: darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.12)',
          color: darkMode ? 'rgba(126,206,206,0.4)' : 'rgba(13,26,26,0.45)',
          letterSpacing: '1px',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.color = '#e05050';
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(224,80,80,0.3)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.color = darkMode ? 'rgba(126,206,206,0.4)' : 'rgba(13,26,26,0.45)';
          (e.currentTarget as HTMLButtonElement).style.borderColor = darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.12)';
        }}
      >
        <FaSignOutAlt style={{ fontSize: '14px' }} />
        <span>LOGOUT</span>
      </button>
    </aside>
  );
};